import { AxiosError } from 'axios'
import cn from 'classnames'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import ProgressBar from 'ext-ui/components/ProgressBar/ProgressBar'
import CardIconSvg from 'ext-ui/svg/illustration/CardIconSvg'
import ShieldIconSvg from 'ext-ui/svg/illustration/ShieldIconSvg'
import React, { useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { usePutCreditCardV2 } from 'src/api/mutation/creditcards'
import { useSend3DSVerification } from 'src/api/mutation/creditcards'
import { useLazyGetCreditCardById } from 'src/api/query/creditcards'
import { PusherContext } from 'src/app/App'
import { AstradaBinData } from 'src/context/AstradaContext/AstradaContext'
import { PusherEvents } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { PhysicalCardStatus } from 'src/models/creditCard.models'
import { selectUserIdFromState } from 'src/redux/selectors/auth.selectors'
import { selectPusherChannelPrefix } from 'src/redux/selectors/session.selectors'

import Confirm3DSVerification from './Confirm3DSVerification'
import style from './PhysicalCardVerifyStep.style'

type PhysicalCardVerifyStepProps = {
  className?: string
  cardId: string
  eligibilityType: AstradaBinData['eligibility']
  /**
   * Use this callback for card enrollment errors when creating the stub
   * card via the /physical endpoint, or when the card fails via Pusher event
   * during the creation process
   */
  onCardEnrollError?: (error: string) => void
  onVerifyStart?: () => void
  /**
   * Use this callback for when 3ds verification fails
   */
  onVerifyError?: (errorMessage: string) => void
  onVerifyComplete?: (cardId: string) => void
}

/**
 * Render this component to start the 3DS verification process.
 * This is a 'Batteries Included' component which does our entire
 * 3DS automation flow
 *
 */
export default function PhysicalCardVerifyStep(
  props: PhysicalCardVerifyStepProps,
) {
  const {
    className = '',
    cardId,
    onCardEnrollError,
    onVerifyStart,
    onVerifyError,
    onVerifyComplete,
  } = props
  const { analytics } = useAnalyticsContext()
  const translate = useTranslate()
  const { mutateAsync: putCreditCardV2 } = usePutCreditCardV2()
  const pusher = useContext(PusherContext)
  const [verificationId, setVerificationId] = useState<string>('')
  const [failureCount, setFailureCount] = useState<number>(0)

  const userId = useSelector(selectUserIdFromState)
  const pusherChannelPrefix = useSelector(selectPusherChannelPrefix)

  const { mutateAsync: send3DSVerification } = useSend3DSVerification()
  const { getCardById } = useLazyGetCreditCardById()

  // onMount Effect - This runs right away
  useEffect(() => {
    const startVerification = async () => {
      const creditCard = await getCardById(cardId)
      if (
        creditCard.physicalCard.status === PhysicalCardStatus.NeedsVerification
      ) {
        const freshVerificationId = creditCard.physicalCard.verificationId
        if (freshVerificationId) {
          setVerificationId(freshVerificationId)
          return
        }
      }

      if (!verificationId) {
        onVerifyStart?.()
        putCreditCardV2({
          creditCardId: cardId,
          endpoint: 'physical',
        })
          .then((response) => {
            // If the response contains a verification ID, then
            // use setVerificationId(response.verificationId)
            if (response?.creditCard?.verificationId) {
              // TODO: Check and test to make sure this works as expected. Need a card to fully test this
              // use case but I don't have one at the moment
              setVerificationId(response.creditCard.verificationId)
            }
          })
          // We use a promise catch here because we want everything here to run async.
          .catch((error) => {
            const unwrappedError = error as AxiosError

            //  Casting here as to appease typescript. It is hard to see if
            //  there is a generic to send in to appease typescript so
            // We can cast here for the time being
            const errorData = unwrappedError.response?.data as {
              error?: string
            }
            // Code to run if the initial /physical API call fails

            const defaultErrorMessage = translate(
              'Card enrollment failed - Try again later',
            )

            onCardEnrollError?.(errorData.error || defaultErrorMessage)
          })
      }

      if (pusher) {
        const channel = pusher.subscribe(`${pusherChannelPrefix}${userId}`)
        // Wait for pusher event.
        channel.bind(
          PusherEvents.PhysicalAccount,
          async (data: { id: string; action: string }) => {
            if (data.action === 'UPDATED' && !verificationId) {
              // TODO: Remove this call. Use the existing verificationId on the GET creditCard
              const response = await send3DSVerification(data.id)

              setVerificationId(response.verificationId)
            }

            if (data.action === 'FAILED') {
              onCardEnrollError?.('ERROR_FAILED')
            }
          },
        )
      }
    }

    startVerification()

    return () => {
      if (pusher) {
        const channel = pusher.subscribe(`${pusherChannelPrefix}${userId}`)
        channel.unbind(PusherEvents.PhysicalAccount)
      }
    }
  }, [verificationId])

  // Message event handler for pop up window - runs onMount
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.data.type === '3ds.verificationComplete') {
        onVerifyComplete?.(cardId)
      } else if (event.data.type === '3ds.verificationFailed') {
        analytics.track('3ds.verificationFailed')
        const totalFailureCount = failureCount + 1
        if (totalFailureCount >= 2) {
          // Display an error state and stop trying
          // This 'ERROR_FAILED' string is a constant that will trigger a generic message
          // in an AlertPanel component to show up on the previous screen

          onVerifyError?.('ERROR_FAILED')
          return
        } else {
          setVerificationId('')
          setFailureCount(totalFailureCount)
        }
      }
    }

    window.addEventListener('message', onMessage)

    return () => {
      window.removeEventListener('message', onMessage)
    }
  }, [failureCount])

  return (
    <div className={cn('PhysicalCardVerifyStep', className)} css={style}>
      {verificationId ? (
        <Confirm3DSVerification
          verificationId={verificationId}
          isRetry={failureCount > 0}
        />
      ) : (
        <div className="flex-column align-center justify-center gap-10">
          <div className="cardIcon-background flex-column align-center justify-center">
            <CardIconSvg />
          </div>

          <ProgressBar variant="indeterminate" />
          <div className="profit flex-column gap-3">
            <p className="font-h3">
              {`🎨  ${translate('Painting profit rainbows...')}`}
            </p>
            <p className="add-color">
              {translate('Adding color to your portfolio')}
            </p>
          </div>
          <AlertPanel>
            <div className="flex-row align-start gap-3">
              <ShieldIconSvg />
              <div className="flex-column gap-2">
                <p className="font-bold">{translate('Secure Processing')}</p>
                <p className="alert-text">
                  {translate(
                    'Your card information is encrypted and processed securely. We never store your full card details.',
                  )}
                </p>
              </div>
            </div>
          </AlertPanel>
        </div>
      )}
    </div>
  )
}
